import { groupBookingRanges } from '../utils/bookingRanges';
import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  CreditCard,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  QrCode,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { getMyVenues } from '../services/venueService';
import { getVenueCourts } from '../services/courtService';
import { createManualBooking } from '../services/bookingService';
import { formatCurrency } from '../utils/formatters';

function localDateInputValue(date = new Date()) {
  const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 10);
}

function slotTimeMinutes(time) {
  const [value = '0', minutes = '0'] = String(time || '').split(':');
  const [days, hours] = value.includes('.') ? value.split('.').map(Number) : [0, Number(value)];
  return (days * 24 + hours) * 60 + Number(minutes);
}

function slotDateTime(date, time) {
  const [year, month, day] = date.split('-').map(Number);
  const instant = new Date(Date.UTC(year, month - 1, day, 0, slotTimeMinutes(time)));
  return instant.toISOString().slice(0, 19);
}

const PAYMONGO_API_BASE = 'https://api.paymongo.com/v1';

async function payMongoRequest(path, publicKey, body) {
  const response = await fetch(`${PAYMONGO_API_BASE}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Basic ${window.btoa(`${publicKey}:`)}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json();
  if (!response.ok) {
    throw new Error(
      payload?.errors?.[0]?.detail ||
      payload?.errors?.[0]?.code ||
      'PayMongo could not start the QR Ph payment.'
    );
  }
  return payload;
}

function apiErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

export default function ManualBooking() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const canDiscount = ['CourtOwner', 'VenueManager'].includes(auth?.user?.role);
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const discountInvalid = canDiscount && discountType && (!Number.isFinite(Number(discountValue)) || Number(discountValue) <= 0
    || (discountType === 'Percentage' && Number(discountValue) >= 100));
  const discountFields = useMemo(() => canDiscount && discountType
    ? { discountType, discountValue: Number(discountValue) } : {}, [canDiscount, discountType, discountValue]);
  const [venues, setVenues] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [calculatedBreakdown, setCalculatedBreakdown] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searchedEmail, setSearchedEmail] = useState('');
  const [isRegistered, setIsRegistered] = useState(null);
  const [qrPayment, setQrPayment] = useState(null);
  const [form, setForm] = useState({
    venueId: '',
    courtId: '',
    date: localDateInputValue(),
    playerEmail: '',
    paymentMethod: 'Cash',
    notes: '',
  });

  useEffect(() => { loadVenues(); }, []);

  async function loadVenues() {
    try {
      const data = await getMyVenues();
      setVenues(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load venues'); }
    finally { setLoading(false); }
  }

  async function loadCourts(venueId) {
    try {
      const data = await getVenueCourts(venueId);
      setCourts(Array.isArray(data) ? data : []);
    } catch { setCourts([]); }
  }

  const update = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: val };
      if (field === 'venueId') { next.courtId = ''; loadCourts(val); }
      return next;
    });
    if (field === 'venueId' || field === 'courtId' || field === 'date') {
      setSelectedSlots([]);
      setAvailableSlots([]);
    }
  };

  const canLoadSlots = form.venueId && form.courtId && form.date;

  useEffect(() => {
    if (!canLoadSlots) { setAvailableSlots([]); setSelectedSlots([]); return; }
    loadSlots();
  }, [form.courtId, form.date, form.venueId]);

  async function loadSlots() {
    setSlotsLoading(true);
    setSelectedSlots([]);
    try {
      const res = await api.get(`/venues/${form.venueId}/availability`, {
        params: { date: form.date },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      const courtData = data.find((c) => (c.courtId || c.id) === form.courtId);
      const rawSlots = (courtData?.availableSlots || []).filter((s) => s.isAvailable !== false);

      // Filter out past slots if today
      const now = new Date();
      const isToday = form.date === now.toISOString().split('T')[0];
      const filtered = isToday
        ? rawSlots.filter((s) => {
            const minutes = slotTimeMinutes(s.startTime);
            if (Number.isNaN(minutes)) return true;
            return minutes > now.getHours() * 60 + now.getMinutes();
          })
        : rawSlots;

      setAvailableSlots(filtered);
    } catch { setAvailableSlots([]); }
    finally { setSlotsLoading(false); }
  }

  async function searchPlayers(query) {
    if (query.length < 2) { setSearchResults([]); setShowResults(false); return; }
    try {
      const res = await api.get('/users/search', { params: { q: query } });
      const users = Array.isArray(res.data?.users) ? res.data.users : [];
      setSearchResults(users);
      setShowResults(users.length > 0);
    } catch { setSearchResults([]); }
  }

  let searchTimer;
  function handleEmailChange(val) {
    setForm((prev) => ({ ...prev, playerEmail: val }));
    setIsRegistered(null);
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => searchPlayers(val), 300);
  }

  function toggleSlot(slot) {
    setSelectedSlots((prev) => {
      const key = (s) => `${s.startTime}-${s.endTime}`;
      if (prev.some((s) => key(s) === key(slot)))
        return prev.filter((s) => key(s) !== key(slot));
      const limit = selectedVenue?.maxBookingHoursPerTransaction;
      const durationHours = (value) => {
        const start = slotTimeMinutes(value.startTime);
        const end = slotTimeMinutes(value.endTime);
        return ((end <= start ? end + 24 * 60 : end) - start) / 60;
      };
      const selectedHours = prev.reduce((total, value) => total + durationHours(value), 0);
      if (limit && selectedHours + durationHours(slot) > limit) {
        toast.error(`This venue allows up to ${limit} hour${limit === 1 ? '' : 's'} per booking transaction.`);
        return prev;
      }
      return [...prev, slot].sort((a, b) =>
        (a.startTime || '').localeCompare(b.startTime || '')
      );
    });
  }

  const selectedVenue = venues.find((v) => (v._id || v.id) === form.venueId);

  useEffect(() => {
    if (selectedVenue?.supportsQrPhPayments === false && form.paymentMethod === 'QRPh') {
      setForm((current) => ({ ...current, paymentMethod: 'Cash' }));
    }
  }, [selectedVenue, form.paymentMethod]);

  const sortedSelected = [...selectedSlots].sort((a, b) =>
    (a.startTime || '').localeCompare(b.startTime || '')
  );

  const slotDurationHours = (slot) => {
    const start = slotTimeMinutes(slot.startTime);
    const rawEnd = slotTimeMinutes(slot.endTime);
    const end = rawEnd <= start ? rawEnd + 24 * 60 : rawEnd;
    return Math.max((end - start) / 60, 0.5);
  };

  // Base estimate used only while the exact pricing-rule calculation loads.
  const baseEstimate = selectedSlots.length > 0
    ? selectedSlots.reduce(
        (total, slot) => total + (selectedVenue?.pricePerHour || 0) * slotDurationHours(slot),
        0
      )
    : null;

  // Fetch calculated price from API (uses pricing rules)
  useEffect(() => {
    if (!form.courtId || selectedSlots.length === 0 || !form.date || discountInvalid) {
      setCalculatedPrice(null); setCalculatedBreakdown(null); setPriceLoading(false);
      return;
    }
    let active = true;
    setCalculatedPrice(null); setCalculatedBreakdown(null); setPriceLoading(true);
    const ranges = groupBookingRanges(selectedSlots.map((slot) => ({ courtId: form.courtId,
      startTime: slotDateTime(form.date, slot.startTime), endTime: slotDateTime(form.date, slot.endTime) })));
    Promise.all(ranges.map((range) => api.get('/bookings/calculate-price', { params: { ...range, ...discountFields } }))).then((responses) => {
      if (active) {
        setCalculatedBreakdown({
          originalRentalAmount: responses.reduce((sum, r) => sum + Number(r.data.originalAmount ?? r.data.total ?? 0), 0),
          promotionDiscountAmount: responses.reduce((sum, r) => sum + Number(r.data.promotionDiscountAmount || 0), 0),
          promotionNames: [...new Set(responses.map((r) => r.data.promotionName).filter(Boolean))],
        });
        setCalculatedPrice(responses.reduce(
          (total, response) => total + Number(response.data.total || 0),
          0
        ));
      }
    }).catch((error) => {
      if (active && discountType) toast.error(apiErrorMessage(error, 'Could not calculate this discount.'));
      if (active) { setCalculatedPrice(null); setCalculatedBreakdown(null); }
    }).finally(() => { if (active) setPriceLoading(false); });
    return () => { active = false; };
  }, [selectedSlots, form.courtId, form.date, discountType, discountValue, canDiscount, discountInvalid, discountFields]);

  const displayPrice = calculatedPrice ?? baseEstimate;
  const rentalAmount = paymentQuote?.rentalAmount ?? displayPrice ?? 0;
  const discountBreakdown = form.paymentMethod === 'QRPh' ? paymentQuote : calculatedBreakdown;
  const promotionDiscount = Number(discountBreakdown?.promotionDiscountAmount || 0);
  const originalRental = discountBreakdown?.originalRentalAmount ?? rentalAmount;
  const convenienceFee = form.paymentMethod === 'QRPh'
    ? paymentQuote?.paymentFee ?? 0
    : 0;
  const totalAmount = form.paymentMethod === 'QRPh'
    ? paymentQuote?.totalAmount ?? rentalAmount
    : rentalAmount;

  // QR Ph can include a player-paid convenience fee depending on the venue's
  // fee policy. Cash is collected directly by the venue and has no platform fee.
  useEffect(() => {
    if (form.paymentMethod !== 'QRPh' ||
        !form.courtId || selectedSlots.length === 0 || !form.date || discountInvalid) {
      setPaymentQuote(null);
      setQuoteLoading(false);
      return undefined;
    }

    let active = true;
    setQuoteLoading(true);
    setPaymentQuote(null);
    api.post('/payments/quote', {
      paymentMethod: 'QRPh',
      ...discountFields,
      slots: groupBookingRanges(selectedSlots.map((slot) => ({
        courtId: form.courtId,
        startTime: slotDateTime(form.date, slot.startTime),
        endTime: slotDateTime(form.date, slot.endTime),
      }))),
    }).then((response) => {
      if (active) setPaymentQuote(response.data);
    }).catch((error) => {
      if (active && discountType) toast.error(apiErrorMessage(error, 'Could not calculate this discount.'));
      if (active) setPaymentQuote(null);
    }).finally(() => {
      if (active) setQuoteLoading(false);
    });

    return () => { active = false; };
  }, [form.paymentMethod, form.courtId, form.date, selectedSlots, discountType, discountValue, canDiscount, discountInvalid, discountFields]);

  async function abandonQrPayment(bookingId) {
    await api.post(`/payments/paymongo/intents/${bookingId}/abandon`);
  }

  async function startQrPhPayment(booking, bookings = [booking]) {
    const savedBookingDate = booking?.startTime?.slice(0, 10) || form.date;
    setQrPayment({ booking, bookings, bookingDate: savedBookingDate, loading: true, error: '' });

    try {
      const { data: intent } = await api.post(`/payments/paymongo/intents/${booking.id}`, {
        paymentMethod: 'QRPh',
        bookingIds: bookings.slice(1).map((item) => item.id),
      });

      let qrImage = intent.qrCodeImage;
      let testUrl = intent.qrPhTestUrl || null;
      if (!qrImage) {
        const paymentMethodResponse = await payMongoRequest('/payment_methods', intent.publicKey, {
          data: {
            attributes: {
              type: 'qrph',
              ...(intent.expirySeconds ? { expiry_seconds: intent.expirySeconds } : {}),
            },
          },
        });
        const attachResponse = await payMongoRequest(
          `/payment_intents/${intent.paymentIntentId}/attach`,
          intent.publicKey,
          {
            data: {
              attributes: {
                payment_method: paymentMethodResponse.data.id,
                client_key: intent.clientKey,
              },
            },
          }
        );
        const code = attachResponse.data?.attributes?.next_action?.code;
        qrImage = code?.image_url;
        testUrl = code?.test_url || attachResponse.data?.attributes?.next_action?.test_url || null;
      }

      if (!qrImage) throw new Error('PayMongo did not return a QR Ph image.');
      const qrRentalAmount = bookings.reduce(
        (total, item) => total + Number(item?.totalAmount || 0),
        0
      ) || Number(rentalAmount ?? 0);
      const qrTotalAmount = Number(intent.amount ?? qrRentalAmount);
      setQrPayment({
        booking,
        bookings,
        bookingDate: savedBookingDate,
        amount: qrTotalAmount,
        rentalAmount: qrRentalAmount,
        originalRentalAmount: bookings.reduce((sum, item) => sum + Number(item.originalAmount ?? item.totalAmount ?? 0), 0),
        promotionDiscountAmount: bookings.reduce((sum, item) => sum + Number(item.promotionDiscountAmount || 0), 0),
        promotionNames: [...new Set(bookings.map((item) => item.promotionName).filter(Boolean))],
        convenienceFee: Math.max(qrTotalAmount - qrRentalAmount, 0),
        qrImage,
        testUrl,
        isTestMode: intent.isTestMode === true,
        loading: false,
        checking: false,
        paid: false,
        error: '',
      });
    } catch (error) {
      await Promise.allSettled(bookings.map((item) => abandonQrPayment(item.id)));
      setQrPayment({
        booking,
        bookings,
        bookingDate: savedBookingDate,
        loading: false,
        released: true,
        error: `${apiErrorMessage(error, 'Unable to start QR Ph payment.')} The unpaid booking was released.`,
      });
    }
  }

  async function checkQrPaymentStatus() {
    if (!qrPayment?.booking?.id) return;
    setQrPayment((current) => ({ ...current, checking: true, error: '' }));
    try {
      const { data } = await api.get(`/payments/paymongo/intents/${qrPayment.booking.id}`);
      if (data.isFailed === true || data.status === 'failed') {
        setQrPayment((current) => ({
          ...current,
          checking: false,
          released: true,
          error: 'The payment was not completed. The unpaid booking was released.',
        }));
        return;
      }
      setQrPayment((current) => ({
        ...current,
        checking: false,
        paid: data.isPaid === true,
        status: data.status,
        error: data.isPaid ? '' : 'Payment is still pending. Complete the payment, then check again.',
      }));
    } catch (error) {
      setQrPayment((current) => ({
        ...current,
        checking: false,
        error: apiErrorMessage(error, 'Unable to check the payment status. Please try again.'),
      }));
    }
  }

  async function cancelQrPayment() {
    if (!qrPayment?.booking?.id) return;
    try {
      await Promise.allSettled(
        (qrPayment.bookings || [qrPayment.booking]).map((item) =>
          abandonQrPayment(item.id)
        )
      );
      toast.success('The unpaid booking was released');
      setQrPayment(null);
      await loadSlots();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Unable to release this booking. Check its payment status.'));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (priceLoading || quoteLoading || (form.paymentMethod === 'QRPh' ? !paymentQuote : calculatedPrice == null)) { toast.error('Wait for the exact booking price before continuing.'); return; }
    if (selectedSlots.length === 0) { toast.error('Select at least one time slot'); return; }
    if (!form.playerEmail) { toast.error('Enter player email'); return; }

    setSubmitting(true);
    try {
      const bookings = [];
      const ranges = groupBookingRanges(sortedSelected.map((slot) => ({ courtId: form.courtId,
        startTime: slotDateTime(form.date, slot.startTime), endTime: slotDateTime(form.date, slot.endTime) })));
      for (const range of ranges) {
        bookings.push(await createManualBooking({
          ...discountFields,
          playerEmail: form.playerEmail,
          courtId: form.courtId,
          startTime: range.startTime,
          endTime: range.endTime,
          notes: form.notes || null,
          paymentMethod: form.paymentMethod,
        }));
      }
      if (form.paymentMethod === 'QRPh') {
        await startQrPhPayment(bookings[0], bookings);
      } else {
        toast.success('Cash booking created successfully');
        const savedBookingDate = bookings[0]?.startTime?.slice(0, 10) || form.date;
        navigate(`/bookings?date=${encodeURIComponent(savedBookingDate)}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create booking');
    } finally { setSubmitting(false); }
  }



  function formatSlotTime(time) {
    if (!time) return '';
    const totalMinutes = slotTimeMinutes(time);
    if (Number.isNaN(totalMinutes)) return time;
    const h = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
      </div>
    );
  }

  if (qrPayment) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={cancelQrPayment} className="btn-secondary p-2" disabled={qrPayment.loading}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#1F2937]">QR Ph Payment</h1>
            <p className="text-sm text-[#64748B]">Complete the payment before confirming the manual booking</p>
          </div>
        </div>

        <div className="card max-w-xl mx-auto p-6 sm:p-8 text-center">
          {qrPayment.loading ? (
            <div className="py-14">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
              <p className="mt-4 font-medium text-[#1F2937]">Generating QR Ph code...</p>
              <p className="mt-1 text-sm text-[#64748B]">Please keep this page open.</p>
            </div>
          ) : qrPayment.paid ? (
            <div className="py-8">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-9 w-9 text-green-600" />
              </div>
              <h2 className="mt-5 text-xl font-bold text-[#1F2937]">Payment confirmed</h2>
              <p className="mt-2 text-sm text-[#64748B]">The booking is confirmed and the player has been notified.</p>
              <button
                type="button"
                onClick={() => navigate(`/bookings?date=${encodeURIComponent(qrPayment.bookingDate)}`)}
                className="btn-primary mt-6 w-full"
              >
                View booking
              </button>
            </div>
          ) : qrPayment.released ? (
            <div className="py-8">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <XCircle className="h-9 w-9 text-red-600" />
              </div>
              <h2 className="mt-5 text-xl font-bold text-[#1F2937]">Payment unsuccessful</h2>
              <p className="mt-2 text-sm text-[#64748B]">{qrPayment.error}</p>
              <button
                type="button"
                onClick={async () => {
                  setQrPayment(null);
                  await loadSlots();
                }}
                className="btn-primary mt-6 w-full"
              >
                Try another booking
              </button>
            </div>
          ) : (
            <>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#E8F4F4]">
                <QrCode className="h-7 w-7 text-[#008B8B]" />
              </div>
              <h2 className="mt-4 text-xl font-bold text-[#1F2937]">Scan to pay with QR Ph</h2>
              <p className="mt-1 text-sm text-[#64748B]">Use a QR Ph-compatible banking or e-wallet app.</p>
              {qrPayment.amount != null && (
                <div className="mx-auto mt-5 max-w-sm rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-left">
                  <div className="flex items-center justify-between text-sm text-[#64748B]">
                    <span>Court rental</span>
                    <span className="font-medium text-[#1F2937]">{formatCurrency(qrPayment.originalRentalAmount ?? qrPayment.rentalAmount)}</span>
                  </div>
                  {qrPayment.promotionDiscountAmount > 0 && <div className="mt-2 flex items-center justify-between gap-3 text-sm text-emerald-700"><span>Booking discount{qrPayment.promotionNames?.length ? ` — ${qrPayment.promotionNames.join(', ')}` : ''}</span><span>−{formatCurrency(qrPayment.promotionDiscountAmount)}</span></div>}
                  {qrPayment.convenienceFee > 0 && (
                    <div className="mt-2 flex items-center justify-between text-sm text-[#64748B]">
                      <span>Convenience Fee</span>
                      <span className="font-medium text-[#1F2937]">{formatCurrency(qrPayment.convenienceFee)}</span>
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between border-t border-[#CBD5E1] pt-3">
                    <span className="font-semibold text-[#1F2937]">Total to pay</span>
                    <span className="text-xl font-bold text-[#264D59]">{formatCurrency(qrPayment.amount)}</span>
                  </div>
                </div>
              )}
              <div className="mx-auto mt-5 max-w-xs rounded-2xl border border-[#CBD5E1] bg-white p-4">
                <img src={qrPayment.qrImage} alt="QR Ph payment code" className="w-full" />
              </div>

              {qrPayment.isTestMode && qrPayment.testUrl && (
                <a
                  href={qrPayment.testUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#008B8B] hover:underline"
                >
                  Simulate payment in PayMongo <ExternalLink size={14} />
                </a>
              )}

              {qrPayment.error && (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  {qrPayment.error}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
                <button type="button" onClick={cancelQrPayment} className="btn-secondary flex-1" disabled={qrPayment.checking}>
                  Cancel and release
                </button>
                <button
                  type="button"
                  onClick={checkQrPaymentStatus}
                  className="btn-primary flex flex-1 items-center justify-center gap-2"
                  disabled={qrPayment.checking}
                >
                  <RefreshCw size={16} className={qrPayment.checking ? 'animate-spin' : ''} />
                  {qrPayment.checking ? 'Checking...' : 'I have paid'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="btn-secondary p-2"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Manual Booking</h1>
          <p className="text-sm text-[#64748B]">Create a booking on behalf of a player</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: venue, court, date */}
        <div className="lg:col-span-1 card p-6 space-y-4 h-fit">
          <h3 className="text-sm font-semibold text-[#1F2937] uppercase">Select Details</h3>
          <div>
            <label className="block text-sm font-medium text-[#64748B] mb-1">Venue *</label>
            <select value={form.venueId} onChange={update('venueId')} className="input-field">
              <option value="">Select venue</option>
              {venues.map((v) => (
                <option key={v._id || v.id} value={v._id || v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#64748B] mb-1">Court *</label>
            <select value={form.courtId} onChange={update('courtId')} className="input-field" disabled={!form.venueId}>
              <option value="">Select court</option>
              {courts.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.name} ({c.courtType})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#64748B] mb-1">Date *</label>
            <input type="date" value={form.date} onChange={update('date')} className="input-field" />
          </div>

          {selectedVenue && (
            <div className="p-3 bg-[#F9FAFB] rounded-xl">
              <p className="text-sm text-[#64748B]">Base price: <span className="text-[#1F2937] font-semibold">{formatCurrency(selectedVenue.pricePerHour)}</span> / hour</p>
              {displayPrice != null && (
                <p className="text-sm text-[#64748B] mt-1">Rental estimate: <span className="text-[#264D59] font-bold">{formatCurrency(displayPrice)}</span></p>
              )}
            </div>
          )}
        </div>

        {/* Right column: time slots */}
        <div className="lg:col-span-2 card p-6 space-y-4">
          {!canLoadSlots ? (
            <p className="text-[#64748B] text-sm">Select a venue, court, and date to see available time slots.</p>
          ) : slotsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#264D59] border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-[#64748B] text-sm">Loading slots...</span>
            </div>
          ) : availableSlots.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="mx-auto h-8 w-8 text-[#94A3B8]" />
              <p className="mt-3 text-[#64748B] text-sm">No available time slots for this date.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div><h3 className="text-sm font-semibold text-[#1F2937] uppercase">Available Time Slots</h3>{selectedVenue?.maxBookingHoursPerTransaction && <p className="mt-1 text-xs text-[#64748B]">Maximum {selectedVenue.maxBookingHoursPerTransaction} hour{selectedVenue.maxBookingHoursPerTransaction === 1 ? '' : 's'} per transaction</p>}</div>
                <button onClick={loadSlots} className="text-xs text-[#E6FF00] hover:underline">
                  Refresh
                </button>
              </div>
              {availableSlots.some((slot) => slot.promotions?.length) && <p className="text-xs text-emerald-800">Promotions available. The best eligible offer is applied in the breakdown below. Fixed discounts apply once per continuous booking, not each hour.</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {availableSlots.map((slot, i) => {
                  const key = `${slot.startTime}-${slot.endTime}-${i}`;
                  const isSelected = selectedSlots.some(
                    (s) => s.startTime === slot.startTime && s.endTime === slot.endTime
                  );
                  return (
                    <button
                      key={key}
                      onClick={() => toggleSlot(slot)}
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors text-center ${
                        isSelected
                          ? 'bg-[#264D59] text-white border-[#264D59] font-bold'
                          : 'bg-[#F9FAFB] text-[#1F2937] border-[#CBD5E1] hover:border-[#264D59]'
                      }`}
                    >
                      <span className="block">{formatSlotTime(slot.startTime)}</span>
                      <span className="block text-xs opacity-60">—</span>
                      <span className="block text-xs">{formatSlotTime(slot.endTime)}</span>
                      <span className="mt-2 block font-semibold">{formatCurrency(slot.pricePerHour ?? selectedVenue?.pricePerHour ?? 0)} / hr</span>
                      {(slot.promotions || []).length > 0 && <span className={`mt-1 block rounded px-1 py-1 text-xs ${isSelected ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-800'}`} title={slot.promotions.map((p) => p.name).join(', ')}>
                        {slot.promotions[0].discountType === 'Percentage' ? `${slot.promotions[0].discountValue}% off available` : `${formatCurrency(slot.promotions[0].discountValue)} off per booking`}
                      </span>}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Player details — shown only when slots are selected */}
          {selectedSlots.length > 0 && (
            <>
              <div className="border-t border-[#CBD5E1] pt-6 mt-4">
                <h3 className="text-sm font-semibold text-[#1F2937] uppercase mb-4">Player & Payment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-[#64748B] mb-1">
                      <Mail className="inline h-3.5 w-3.5 mr-1" />Player Email *
                    </label>
                    <input
                      type="email"
                      value={form.playerEmail}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      className="input-field"
                      placeholder="Type email to search registered players..."
                      autoComplete="off"
                    />
                    {showResults && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-[#CBD5E1] rounded-xl shadow-xl max-h-48 overflow-y-auto">
                        {searchResults.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, playerEmail: u.email }));
                              setIsRegistered(true);
                              setShowResults(false);
                              setSearchResults([]);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm hover:bg-[#F9FAFB] text-[#1F2937] border-b border-[#CBD5E1] last:border-0"
                          >
                            <span className="font-medium">{u.firstName} {u.lastName}</span>
                            <span className="text-[#64748B] ml-2">{u.email}</span>
                            <span className="float-right text-xs text-green-400">Registered</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {isRegistered === true && (
                      <p className="mt-1 text-xs text-green-400">✓ Registered player</p>
                    )}
                    {isRegistered === false && form.playerEmail.length > 0 && (
                      <p className="mt-1 text-xs text-yellow-400">New player — an invite will be sent</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#64748B] mb-1">
                      <CreditCard className="inline h-3.5 w-3.5 mr-1" />Payment Method *
                    </label>
                    <select value={form.paymentMethod} onChange={update('paymentMethod')} className="input-field">
                      <option value="Cash">Cash</option>
                      {selectedVenue?.supportsQrPhPayments !== false && <option value="QRPh">QR Ph</option>}
                    </select>
                  </div>
                </div>

                {canDiscount && <div className="mt-4 rounded-xl border border-[#DDE7EB] bg-[#F8FAFC] p-4">
                  <h3 className="font-semibold text-[#163E4A]">Booking discount</h3>
                  <p className="mt-1 text-xs text-[#64748B]">Optional owner discount for this reservation. Replaces automatic promotions. Fixed discounts apply once per continuous booking; separate time ranges receive separate discounts. Rental must remain at least ₱1.</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="text-sm">Discount type<select aria-label="Booking discount type" className="input-field mt-1" value={discountType} onChange={(e) => setDiscountType(e.target.value)}>
                      <option value="">Use automatic promotions</option>
                      <option value="Percentage">Percentage (%)</option>
                      <option value="FixedAmount">Fixed amount (₱ per booking)</option>
                    </select></label>
                    {discountType && <label className="text-sm">Discount amount<input aria-label="Booking discount amount" className="input-field mt-1" type="number" min="0.01" step="0.01" max={discountType === 'Percentage' ? '99.99' : undefined} value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} /></label>}
                  </div>
                  {discountInvalid && <p role="alert" className="mt-2 text-xs text-red-600">Enter a positive discount; percentage must be below 100%.</p>}
                </div>}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-[#64748B] mb-1">
                    <FileText className="inline h-3.5 w-3.5 mr-1" />Notes
                  </label>
                  <textarea value={form.notes} onChange={update('notes')} className="input-field" rows={2} placeholder="Optional notes..." />
                </div>

                <div className="mt-4 p-3 bg-[#F9FAFB] rounded-xl flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="text-[#1F2937] font-medium">
                      {sortedSelected.map((slot) =>
                        `${formatSlotTime(slot.startTime)}–${formatSlotTime(slot.endTime)}`
                      ).join(', ')}
                    </span>
                    <span className="text-[#64748B] ml-2">
                      · {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} selected
                    </span>
                  </div>
                </div>

                <div className="mt-3 rounded-xl border border-[#E2E8F0] bg-[#F9FAFB] p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#64748B]">Court rental</span>
                    <span className="font-semibold text-[#1F2937]">{formatCurrency(promotionDiscount > 0 ? originalRental : rentalAmount)}</span>
                  </div>
                  {promotionDiscount > 0 && <div className="mt-2 flex items-center justify-between gap-3 text-sm text-emerald-700"><span>Booking discount{discountBreakdown?.promotionNames?.length ? ` — ${discountBreakdown.promotionNames.join(', ')}` : ''}</span><span className="font-semibold">−{formatCurrency(promotionDiscount)}</span></div>}
                  {(priceLoading || quoteLoading) && <p className="mt-2 text-xs text-[#64748B]">Updating price and offers…</p>}
                  {form.paymentMethod === 'QRPh' && convenienceFee > 0 && (
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="text-[#64748B]">Convenience Fee</span>
                      <span className="font-semibold text-[#1F2937]">{formatCurrency(convenienceFee)}</span>
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between border-t border-[#CBD5E1] pt-3">
                    <span className="text-sm font-semibold text-[#1F2937]">Total</span>
                    <span className="text-lg font-bold text-[#264D59]">
                      {quoteLoading ? 'Calculating...' : formatCurrency(totalAmount)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => navigate('/bookings')} className="btn-secondary">Cancel</button>
                <button onClick={handleSubmit} disabled={submitting || quoteLoading || priceLoading || (form.paymentMethod === 'QRPh' ? !paymentQuote : calculatedPrice == null) || !form.playerEmail} className="btn-primary">
                  {submitting
                    ? (form.paymentMethod === 'QRPh' ? 'Preparing QR Ph...' : 'Creating...')
                    : (form.paymentMethod === 'QRPh' ? 'Continue to QR Ph' : 'Create Cash Booking')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
